"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, Search, X } from "lucide-react";

export type HomeCompanySearchItem = {
  name: string;
  ticker: string;
  isin: string;
  sector: string;
  slug: string;
};

function normalize(value: string) {
  return value
    .toLocaleLowerCase("pl")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function HomeCompanySearch({
  companies,
  featuredTickers,
}: {
  companies: HomeCompanySearchItem[];
  featuredTickers: string[];
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = normalize(query.trim());

  const visibleCompanies = useMemo(() => {
    if (!normalizedQuery) {
      return featuredTickers
        .map((ticker) => companies.find((company) => company.ticker === ticker))
        .filter((company): company is HomeCompanySearchItem => Boolean(company));
    }

    return companies
      .filter((company) =>
        normalize(
          `${company.name} ${company.ticker} ${company.isin} ${company.sector}`,
        ).includes(normalizedQuery),
      )
      .slice(0, 8);
  }, [companies, featuredTickers, normalizedQuery]);

  return (
    <section
      className="scroll-mt-24 border-y border-white/10 bg-[#101722]/70 px-4 py-14 sm:px-6 lg:px-8"
      id="znajdz-spolke"
    >
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FFD700]">
            Katalog GPW
          </p>
          <h2 className="mt-3 text-3xl font-semibold text-white sm:text-4xl">
            Znajdź spółkę
          </h2>
          <p className="mt-4 leading-7 text-slate-300">
            Wyszukaj emitenta po nazwie, tickerze, ISIN lub sektorze i przejdź
            do terminów raportów oraz oficjalnych źródeł.
          </p>
        </div>

        <div className="relative mt-7 max-w-3xl">
          <label className="sr-only" htmlFor="home-company-search">
            Nazwa spółki, ticker, ISIN lub sektor
          </label>
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-slate-400"
          />
          <input
            className="h-14 w-full rounded-md border border-white/15 bg-[#0e1117] pl-12 pr-14 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-[#3498db] focus:ring-2 focus:ring-[#3498db]/20"
            id="home-company-search"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Np. XTB, CD Projekt, KGHM lub handel detaliczny"
            type="search"
            value={query}
          />
          {query ? (
            <button
              aria-label="Wyczyść wyszukiwanie"
              className="absolute right-2 top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition hover:bg-white/[0.06] hover:text-white"
              onClick={() => setQuery("")}
              title="Wyczyść"
              type="button"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          ) : null}
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <p aria-live="polite" className="text-sm text-slate-400">
            {normalizedQuery
              ? `Znalezione profile: ${visibleCompanies.length}`
              : "Szybkie przejścia do profili spółek"}
          </p>
          <Link
            className="text-sm font-semibold text-[#54afe9] transition hover:text-white"
            href="/spolki"
          >
            Wszystkie spółki
          </Link>
        </div>

        {visibleCompanies.length ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {visibleCompanies.map((company) => (
              <Link
                className="flex min-h-28 items-start justify-between gap-4 rounded-lg border border-white/10 bg-[#0e1117]/75 p-4 transition hover:border-[#3498db]/55 hover:bg-[#3498db]/10"
                href={`/spolki/${company.slug}`}
                key={company.isin}
              >
                <span className="min-w-0">
                  <span className="inline-flex rounded border border-[#3498db]/35 bg-[#3498db]/10 px-2 py-1 font-mono text-xs font-semibold text-[#76c5f5]">
                    {company.ticker}
                  </span>
                  <span className="mt-3 block font-semibold leading-6 text-white">
                    {company.name}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-slate-400">
                    {company.sector}
                  </span>
                </span>
                <ArrowRight aria-hidden="true" className="mt-1 size-4 shrink-0 text-[#3498db]" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-dashed border-white/15 p-5 text-sm leading-7 text-slate-300">
            Nie znaleziono spółki dla tego zapytania. Sprawdź ticker lub otwórz
            pełny katalog GPW.
          </div>
        )}
      </div>
    </section>
  );
}
